import i18n from "i18next";

import { initReactI18next } from "react-i18next";

import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";

import translationEN from "./locales/en.json";
import ConfigService from "./services/ConfigService";
import { fetchCdnJson } from "./api/fetchCdnJson";
import { applyDocumentLanguage } from "./utils/direction";

const supportedLngs = [
  "ar",
  "az",
  "bg",
  "bn",
  "bs",
  "cnr",
  "de",
  "el",
  "en",
  "es",
  "fi",
  "fil",
  "fr",
  "hi",
  "hu",
  "id",
  "it",
  "ja",
  "ka",
  "km",
  "ko",
  "lo",
  "ms",
  "my",
  "nb",
  "nl",
  "pl",
  "pt-BR",
  "pt",
  "ru",
  "sr",
  "sw",
  "th",
  "tl",
  "tr",
  "uk",
  "uz",
  "vi",
  "zh",
];

const languageAliases: Record<string, string> = { ba: "bs", me: "cnr", no: "nb" };
const resolveLanguage = (lang: string): string =>
  languageAliases[lang] || (/^ar-/i.test(lang) ? "ar" : lang);

const requestedLanguages = new Set();

const fetchRemoteTranslationFiles = async (currentLanguage: string) => {
  const resolvedLanguage = resolveLanguage(currentLanguage);
  const promoId = ConfigService.getPromoId();
  if (!promoId || requestedLanguages.has(`${promoId}:${resolvedLanguage}`)) return;
  requestedLanguages.add(`${promoId}:${resolvedLanguage}`);

  try {
    const translations = await fetchCdnJson(
      `${ConfigService.cdnBaseURL}/promo/${promoId}/${resolvedLanguage}.json`
    );

    i18n.addResourceBundle(resolvedLanguage, "translation", translations);
  } catch (err) {
    if (resolvedLanguage !== "en" && !requestedLanguages.has(`${promoId}:en`)) {
      requestedLanguages.add(`${promoId}:en`);
      try {
        const fallbackTranslations = await fetchCdnJson(
          `${ConfigService.cdnBaseURL}/promo/${promoId}/en.json`
        );
        i18n.addResourceBundle("en", "translation", fallbackTranslations);
      } catch (fallbackErr) {
        console.warn(`couldn't find resources for fallback language (en)`);
      }
    }
  }
};

// Only English is bundled, so it renders with no round trip and doubles as the
// fallbackLng. The other 38 locales are emitted as static files by the
// emit-locales plugin in vite.config.ts and fetched on demand.
const resources = {
  en: {
    translation: translationEN,
  },
};

const urlLng = resolveLanguage(ConfigService.getLng());

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    partialBundledLanguages: true,
    supportedLngs: supportedLngs,
    ...(urlLng ? { lng: urlLng } : {}),
    fallbackLng: "en",
    backend: {
      loadPath: `${import.meta.env.BASE_URL}locales/{{lng}}.json`,
    },
    detection: {
      order: ["querystring", "path", "cookie", "htmlTag"],
      caches: ["cookie"],
    },
    interpolation: {
      escapeValue: false,
    },
    // The app has no root Suspense boundary, so translations resolve by
    // re-render against the bundled English fallback instead of suspending.
    react: {
      useSuspense: false,
      bindI18nStore: "added",
    },
  })
  .then(() => {
    fetchRemoteTranslationFiles(i18n.language);
  });

const applyLanguage = (lang: string) => applyDocumentLanguage(lang, i18n.dir(lang));

if (supportedLngs.includes(urlLng)) applyLanguage(urlLng);

i18n.on("languageChanged", (lang) => {
  applyLanguage(lang);
  fetchRemoteTranslationFiles(resolveLanguage(lang));
});

export default i18n;
